import { type PaginationState, type SortingState } from '@tanstack/react-table'
import { type Dispatch, type SetStateAction, useEffect } from 'react'

import { type NAVv2SingleValueFilters } from 'components/hooks/useGetBrokersMissingPayments'
import { type FilterConfigurations } from 'components/reusable/filters/EnterpriseFiltersView'

type GenericFiltersState = Record<string, string | string[]>

interface SyncFiltersWithUrlProps<
  T extends GenericFiltersState,
  U,
  G extends string
> {
  filters: T
  pagination: PaginationState
  sorting?: SortingState
  setFilters: Dispatch<SetStateAction<T>>
  defaultFilters?: T
  sortableKeys?: Array<keyof U>
  filterConfigurations: FilterConfigurations<T, G>
}

// This component is responsible for helping keep the URL in sync with the filters, sorting and pagination state.
// Can be reused by any tables that need to sync the below types of filters with the URL:
// 1) multi-value filters (e.g. arrays of strings)
// 2) single-value filters (e.g. strings)
// This component uses generic types to allow for any type of filters to be passed in as long as they have a similar
// structure to the filters defined in the filterConfigurations object.
const SyncFiltersWithURL = <
  T extends GenericFiltersState,
  U,
  G extends string
>({
    filters,
    pagination,
    sorting,
    setFilters,
    defaultFilters,
    sortableKeys = [],
    filterConfigurations,
  }: SyncFiltersWithUrlProps<T, U, G>) => {
  // Set all filters from the URL on page load.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    // Track if any filters have been explicitly set in the URL. We cannot rely on state for this because state updates
    // are asynchronous and batched together.
    let hasFilters = false

    Object.values(filterConfigurations).forEach(
      (config) => {
        const { key, isMultiValue } = config
        if (isMultiValue) {
          const values = params.get(`filter[${key.toString()}]`)
          if (values && values.length) {
            hasFilters = true
            setFilters((prev) => ({
              ...prev,
              [key]: values.split(',') as string[],
            }))
          }
        } else {
          const value = params.get(`filter[${key.toString()}]`)
          if (value) {
            hasFilters = true
            setFilters((prev) => ({
              ...prev,
              [key]: value || '',
            }))
          }
        }
      }
    )

    // If no filters are set, use the provided default filters.
    if (!hasFilters) {
      setFilters((prev) => ({
        ...prev,
        ...(defaultFilters || {}),
      }))
    }
  }, [])

  // update the URL as sorting or column filters change
  useEffect(() => {
    const pageURL = new URL(window.location.href)

    sortableKeys.forEach((sortKey) => {
      pageURL.searchParams.delete(`sort[${sortKey.toString()}]`)
    })

    const pageIndex = pagination.pageIndex.toString()
    pageURL.searchParams.set('page', pageIndex)

    Object.values(filterConfigurations).forEach((config) => {
      const { key, isMultiValue } = config
      if (isMultiValue) {
        const filterKey = key as keyof T
        const values = (filters[filterKey] as string[]) || []

        const searchParamsKey = `filter[${filterKey.toString()}]`

        if (values && values.length > 0) {
          pageURL.searchParams.set(searchParamsKey, values.join(',') as string)
        } else {
          pageURL.searchParams.delete(searchParamsKey)
        }
      } else {
        const filterKey = key as keyof NAVv2SingleValueFilters
        const value = filters[filterKey]
        if (value) {
          pageURL.searchParams.set(`filter[${filterKey}]`, value as string)
        } else {
          pageURL.searchParams.delete(`filter[${filterKey}]`)
        }
      }
    })

    if (sorting && sorting.length > 0) {
      const { id, desc } = sorting[0]
      // Tanstack is expecting the column header ID to be 'status'
      const sortKey = id === 'status' ? 'state' : id
      pageURL.searchParams.set(`sort[${sortKey}]`, desc ? 'desc' : 'asc')
    }

    // change the URL of the page without reloading the page
    // Ensure the first state update replaces the current history entry
    if (!window.history.state) {
      window.history.replaceState({}, '', pageURL.toString())
    } else {
      window.history.pushState({}, '', pageURL.toString())
    }
  }, [pagination.pageIndex, sorting, JSON.stringify(filters)])

  // Keep state in sync when the user navigates browser history (back/forward).
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search)

      Object.values(filterConfigurations).forEach((config) => {
        const { key, isMultiValue } = config
        if (isMultiValue) {
          const values = params.get(`filter[${key.toString()}]`)

          if (values) {
            setFilters((prev) => ({
              ...prev,
              [key]: values.split(',') as string[],
            }))
          }
        } else {
          const value = params.get(`filter[${key.toString()}]`)
          if (value) {
            setFilters((prev) => ({
              ...prev,
              [key]: value || '',
            }))
          }
        }
      })
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  return null
}

export default SyncFiltersWithURL
